import QuestionMarkIcon from '@/components/Icon/QuestionMarkIcon';
import MainUserMedia from '@/components/MainUserMedia/MainUserMedia';
import useCalendar from '@/hooks/calendar/useCalendar';
import useCards from '@/hooks/cards/useCards';
import useSpecies from '@/hooks/species/useSpecies';
import useHaptics from '@/hooks/useHaptics';
import cn from '@/lib/cs';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { CSSProperties, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CollectionCardBadges from './CollectionCardBadges/CollectionCardBadges';

export type CollectionCardTarget = 'details' | 'camera';

const CollectionCard = ({
	layoutId,
	speciesId,
	cardsLengthForZIndexFix = 0,
	index,
	target,
	tryUserMediaIndex,
	onClick,
}: {
	layoutId?: string;
	speciesId?: number;
	cardsLengthForZIndexFix?: number;
	index: number;
	target: CollectionCardTarget;
	tryUserMediaIndex?: number;
	onClick?: () => void;
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { withOnButtonPress } = useHaptics();
	const reduceMotion = useReducedMotion();

	const selfRef = useRef<HTMLLIElement>(null);
	const isInView = useInView(selfRef, {
		once: true,
		margin: '50% 50% 50% 50%',
	});

	const { findSpeciesById, getSpeciesRouteById, getMainImageById } =
		useSpecies();
	const { getCardBySpeciesId } = useCards();
	const isCollected = useMemo(
		() => !!getCardBySpeciesId(speciesId ?? 0),
		[getCardBySpeciesId, speciesId],
	);

	// Check if the species is in the calendar, to hide certain information
	const { getIsSpeciesInCalendar } = useCalendar();
	const isSpeciesInCalendar = useMemo(
		() => getIsSpeciesInCalendar(speciesId ?? 0),
		[getIsSpeciesInCalendar, speciesId],
	);

	// Get the species by ID
	const species = useMemo(() => {
		return findSpeciesById(speciesId);
	}, [findSpeciesById, speciesId]);

	// Get the route of the species
	const route = useMemo(() => {
		if (!species) return null;
		return getSpeciesRouteById(species.id);
	}, [getSpeciesRouteById, species]);

	// Is this card on the matching species page?
	const isOnMatchingSpeciesPage = useMemo(() => {
		return location.pathname === route;
	}, [location.pathname, route]);

	// Check if the card is enabled
	const isEnabled = !!route;

	// Get the main image of the species
	const mainImage = species ? getMainImageById(species?.id) : undefined;

	// Random rotation between -5 and 5
	const randomRotation = useMemo(() => {
		return Math.floor(Math.random() * 8) - 4;
	}, []);

	// Get random mask out of the 4 masks
	const randomMask: CSSProperties = useMemo(() => {
		const randomMask = Math.floor(Math.random() * 4) + 1;
		return {
			WebkitMaskImage: `url("/assets/masks/blob_card_${randomMask}.svg")`,
			maskImage: `url("/assets/masks/blob_card_${randomMask}.svg")`,
			maskSize: 'cover',
			maskRepeat: 'no-repeat',
		};
	}, []);

	const shouldShowImage = mainImage && isSpeciesInCalendar;

	return (
		<li ref={selfRef} className="flex items-center justify-center p-0.5">
			<motion.a
				layoutId={reduceMotion ? undefined : layoutId}
				href={route ?? ''}
				aria-disabled={!isEnabled}
				aria-label={`${
					species?.attributes?.titleLong ?? 'Geheime kaart'
				} ${isCollected ? 'verzameld' : 'nog niet verzameld'}`}
				className="relative block aspect-[2/3] w-full max-w-40 rounded-lg focus-visible:p-1"
				style={{
					zIndex: Math.max((cardsLengthForZIndexFix ?? 0) - index, 0),
				}}
				initial={{
					opacity: 0,
					rotate: reduceMotion ? 0 : randomRotation,
				}}
				animate={{
					opacity: isInView ? 1 : 0,
					scale: 0.95,
					transition: {
						duration: 0.5,
					},
				}}
				whileTap={{
					scale: isEnabled ? 0.95 : 1.05,
					rotate: isEnabled ? randomRotation : randomRotation + 6,
				}}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					if (route) {
						if (target === 'details')
							navigate(route ?? '', {
								replace: isOnMatchingSpeciesPage,
							});
						if (target === 'camera')
							navigate('/camera', {
								state: {
									speciesId: speciesId,
								},
								replace: location.pathname === '/camera',
							});
					}
					onClick?.();
				}}
				tabIndex={route ? 0 : -1}
				{...withOnButtonPress}>
				<div
					className="relative flex size-full flex-col overflow-hidden rounded-lg bg-white"
					style={isInView ? randomMask : {}}>
					<div className="relative flex size-full items-center justify-center overflow-hidden">
						{!shouldShowImage && (
							<QuestionMarkIcon className="lg:size-42 mx-auto size-32 text-gray-950/70" />
						)}
						{shouldShowImage && (
							<motion.img
								src={mainImage.thumbnailUrl}
								alt={mainImage.alt}
								loading="lazy"
								className={cn(
									'pointer-events-none absolute inset-0 size-full object-contain scale-90 grayscale-[85%] transition-all',
									isInView ? 'opacity-100' : 'opacity-0',
								)}
							/>
						)}
						<MainUserMedia
							speciesId={species?.id ?? 0}
							isThumbnail
							className="pointer-events-none absolute inset-0 size-full object-cover"
							alt={`Jouw foto van een ${species?.attributes.titleLong}`}
							tryIndex={tryUserMediaIndex}
						/>
					</div>
					<p
						className={cn(
							'shrink-0 ps-1.5 xs:ps-2 sm:ps-3 lg:ps-4 sm:pb-1 md:pb-2 font-brand text-xl first-letter:text-2xl md:first-letter:text-3xl md:text-2xl whitespace-nowrap',
							isOnMatchingSpeciesPage
								? 'text-accentCompat bg-accent'
								: 'text-primary bg-overlay',
						)}>
						{species?.attributes.titleShort}
						{isOnMatchingSpeciesPage && (
							<span className="sr-only">(deze pagina)</span>
						)}
					</p>
				</div>
				{!!species && <CollectionCardBadges species={species} />}
			</motion.a>
		</li>
	);
};

export default CollectionCard;
